import React, { useState, useEffect } from "react";
import { fetchValidationDashboard, fetchCrossStrategySummary, fetchMonitoringSummary } from "../../../lib/researchApi";
import GovernanceBadge from "../shared/GovernanceBadge";
import MetricCard from "../shared/MetricCard";
import OutcomeConfidencePanel from "../shared/OutcomeConfidencePanel";

const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

function pct(n) {
    if (n == null) return "—";
    return `${(n * 100).toFixed(1)}%`;
}

function gradeColor(grade) {
    const colors = { A: "#4ade80", B: "#86efac", C: "#fbbf24", D: "#f87171", F: "#dc2626" };
    return colors[grade] || "var(--savvy-muted)";
}

function StrategyHealthCard({ strategy, dashData, summaryData, monData }) {
    const d = dashData?.[strategy] || {};
    const s = summaryData?.[strategy] || {};
    const mon = monData?.[strategy] || {};
    const grade = s.grade;
    const openAlerts = mon.open_alerts || 0;
    const criticalAlerts = mon.critical_alerts || 0;

    return (
        <div style={{
            background: "var(--savvy-panel)",
            border: d.latest_governance_state === "degraded"
                ? "1px solid rgba(220,38,38,0.4)"
                : "1px solid var(--savvy-border)",
            borderRadius: 10,
            padding: "16px",
        }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--savvy-text)", marginBottom: 4 }}>
                        {STRATEGY_LABELS[strategy] || strategy}
                    </div>
                    <GovernanceBadge state={d.latest_governance_state} />
                </div>
                {grade && (
                    <div style={{
                        fontSize: "28px",
                        fontWeight: 800,
                        color: gradeColor(grade),
                        lineHeight: 1,
                        fontVariantNumeric: "tabular-nums",
                    }}>
                        {grade}
                    </div>
                )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                <div>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 2 }}>Overfit Score</div>
                    <div style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: d.avg_overfit_score > 0.3 ? "#f87171" : d.avg_overfit_score > 0.2 ? "#fbbf24" : "#4ade80",
                    }}>
                        {d.avg_overfit_score != null ? d.avg_overfit_score.toFixed(3) : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 2 }}>Ruin Probability</div>
                    <div style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: d.avg_ruin_probability > 0.2 ? "#f87171" : d.avg_ruin_probability > 0.1 ? "#fbbf24" : "#4ade80",
                    }}>
                        {pct(d.avg_ruin_probability)}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 2 }}>Deployment Readiness</div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--savvy-text)" }}>
                        {s.deployment_readiness_score != null ? (s.deployment_readiness_score * 100).toFixed(0) + "%" : "—"}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: "10px", color: "var(--savvy-muted)", marginBottom: 2 }}>Regime Robustness</div>
                    <div style={{ fontSize: "15px", fontWeight: 600, color: "var(--savvy-text)" }}>
                        {d.avg_regime_robustness_score != null ? (d.avg_regime_robustness_score * 100).toFixed(0) + "%" : "—"}
                    </div>
                </div>
            </div>

            <div style={{ fontSize: "11px", color: "var(--savvy-dim)", marginBottom: 8 }}>
                {d.validation_count ?? "—"} validation{d.validation_count === 1 ? "" : "s"} tracked
            </div>

            {openAlerts > 0 && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    background: criticalAlerts > 0 ? "rgba(220,38,38,0.1)" : "rgba(245,158,11,0.1)",
                    borderRadius: 6,
                    fontSize: "11px",
                    color: criticalAlerts > 0 ? "#f87171" : "#fbbf24",
                }}>
                    ● {openAlerts} open alert{openAlerts !== 1 ? "s" : ""}
                    {criticalAlerts > 0 ? ` · ${criticalAlerts} critical` : ""}
                </div>
            )}

            {s.operational_warnings && s.operational_warnings.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    {s.operational_warnings.slice(0, 2).map((w, i) => (
                        <div key={i} style={{ fontSize: "11px", color: "#fbbf24", marginBottom: 2 }}>
                            ⚠ {w}
                        </div>
                    ))}
                </div>
            )}

            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                <OutcomeConfidencePanel
                    outcomeClassification={s.outcome_classification}
                    evidenceConfidence={s.evidence_confidence}
                    compact
                    showUnavailable
                />
            </div>
        </div>
    );
}

export default function HealthOverviewSection() {
    const [dashData, setDashData] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [monData, setMonData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            fetchValidationDashboard(),
            fetchCrossStrategySummary(),
            fetchMonitoringSummary(),
        ])
            .then(([dash, summary, mon]) => {
                setDashData(dash.strategies || {});
                setSummaryData(summary.strategy_summaries || summary.strategies || {});
                setMonData(mon.strategies || {});
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load health data"))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div style={{ color: "var(--savvy-muted)", padding: 24, textAlign: "center", fontSize: "13px" }}>Loading health overview…</div>;
    if (error) return <div style={{ color: "#f87171", padding: 16, fontSize: "13px" }}>Error: {error}</div>;

    const strategies = Object.keys(dashData || {});
    const totalValidations = strategies.reduce((sum, s) => sum + (dashData[s]?.validation_count || 0), 0);
    const degraded = strategies.filter(s => dashData[s]?.latest_governance_state === "degraded");
    const totalAlerts = strategies.reduce((sum, s) => sum + (monData[s]?.open_alerts || 0), 0);
    const totalCritical = strategies.reduce((sum, s) => sum + (monData[s]?.critical_alerts || 0), 0);

    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
                <MetricCard label="Total Validations" value={totalValidations} />
                <MetricCard label="Strategies Tracked" value={strategies.length} />
                <MetricCard
                    label="Degraded Strategies"
                    value={degraded.length}
                    color={degraded.length > 0 ? "#f87171" : "#4ade80"}
                    warn={degraded.length > 0}
                />
                <MetricCard
                    label="Open Alerts"
                    value={totalAlerts}
                    sub={totalCritical > 0 ? `${totalCritical} critical` : null}
                    color={totalCritical > 0 ? "#f87171" : totalAlerts > 0 ? "#fbbf24" : "#4ade80"}
                    warn={totalCritical > 0}
                />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
                {strategies.map(s => (
                    <StrategyHealthCard
                        key={s}
                        strategy={s}
                        dashData={dashData}
                        summaryData={summaryData}
                        monData={monData}
                    />
                ))}
            </div>
        </div>
    );
}
